import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { Formik, Form } from "formik";
import { HiChevronDown, HiChevronUp } from "react-icons/hi2";
import { Box } from "../Box";
import { Button } from "../Button";
import { Flex } from "../Flex";
import { Input } from "../Input";
import { TextArea } from "../TextArea";
import { Slider } from "../Slider";
import { Paragraph } from "../Paragraph";
import { Loader } from "../Loader";
import { Toggle } from "../Toggle";
import { Checkbox } from "../Checkbox";
import { Tooltip } from "../Tooltip";
import { Icon } from "../Icon";
import { DetailField } from "../DetailField";
import { ProfileOrb } from "../ProfileOrb";
import Select, { Option } from "../Select";
import { useUsers } from "../../data/useUsers";
import { useAccessPermissions } from "../../data/useAccessPermissions";
import { useCreateAccessRole } from "../../data/useCreateAccessRole";
import { useUpdateAccessRole } from "../../data/useUpdateAccessRole";
import { useDeleteAccessRole } from "../../data/useDeleteAccessRole";
import { useAccessRole } from "../../data/useAccessRole";
import { queryClient } from "../../queryClient";
import { showError } from "../../utils/error-handling";
import { notify } from "../../utils/notify";
import { addAccessRoleValidation } from "../../utils/validationSchema";
import { unwrapList } from "../../utils/unwrapList";
import { AccessRole } from "../../types/AccessRole";
import { Permission } from "../../types/Permission";
import { ConfirmModal } from "./ConfirmModal";
import { useAuth } from "../../context/AuthContext";

/**
 * Scope options a Custom Role can be created/edited with. The real
 * `/user/access_role/` response only ever showed raw `scope: "global"` with
 * `scope_display: "Global-only"` on the built-in System roles — read as
 * "global scope is display-labelled 'Global-only' specifically because a
 * System role can't be re-scoped", not as a separate enum value. So the
 * editable set here is just the two scopes a Custom role can actually pick.
 */
const SCOPE_OPTIONS: Option[] = [
  { label: "Global", value: "global" },
  { label: "Per-Division", value: "per_division" },
];

/**
 * Per-instruction, named-System-Role overrides of the otherwise blanket
 * "System Roles are read-only" rule — a stopgap keyed on `role.name` (there's
 * no stable codename to key on instead) rather than a general permission
 * model, deliberately scoped to just these two roles for now:
 * - Admin: Details editable, Permissions stays locked.
 * - Staff: Details editable, Permissions stays locked.
 * System Role permission mappings are not editable (product decision,
 * SEG-S026 review, Oct 2026).
 * Any other System Role (Manager, API Integration, anything unlisted) keeps
 * the original fully-locked behavior. Assigned users is unconditionally
 * editable for every System Role already (see the Assigned users section).
 */
const SYSTEM_ROLE_EDIT_RULES: Record<
  string,
  { details: boolean; permissions: boolean }
> = {
  Admin: { details: true, permissions: false },
  Staff: { details: true, permissions: false },
};

/**
 * Roles are soft-deleted, never hard-deleted; bringing one back is a Tiller
 * support action (SEG-S026). Shared with the Roles list's own delete confirm.
 */
export const DELETE_ROLE_CONFIRM_TEXT =
  "This Role will be deleted and removed from the list. It can only be restored by Tiller support.";

/** Readable message for a failed Role delete; 409 = users still assigned. */
export const deleteRoleErrorMessage = (err: any) =>
  err?.response?.status === 409
    ? "This Role can't be deleted while users are assigned to it. Remove them from the Role first."
    : "A problem occurred while deleting the Role.";

/**
 * The maker/checker break-glass permission. Granting it to any Role other than
 * API Integration (which holds it by design so live integrations keep working)
 * shows a warning (SEG-S026).
 */
const APPROVE_OWN_CODENAME = "mandate.approve_own";
const API_INTEGRATION_ROLE_NAME = "API Integration";

const formatDate = (value?: string | null) =>
  value ? DateTime.fromISO(value).toFormat("dd/MM/yyyy") : "-";

/**
 * `AccessRoleDetail.permissions` is the full catalogue with each entry's own
 * `is_granted` correctly scoped to the Role — confirmed against a real
 * response. Filter on that rather than treating the array itself as "the
 * granted set" (every permission is present regardless of grant state).
 * Keyed on id, not codename — POST and PATCH both take permission ids
 * (confirmed against real request payloads for both).
 */
const grantedPermissionIds = (permissions?: Permission[]): number[] =>
  (permissions || [])
    .filter((permission) => permission.is_granted)
    .map((permission) => permission.id);

/** `AccessRoleDetail.assigned_users` — nested user objects, not bare ids. */
const assignedUserIds = (users?: { id: number }[]): number[] =>
  (users || []).map((user) => user.id);

/**
 * Display-only: the backend calls Applications "Mandate" (resource label,
 * permission names, descriptions), but the product calls them Applications.
 * Swaps the word for on-screen text only — codenames (`mandate.view`) and
 * everything sent to the API are untouched.
 */
const toDisplayLabel = (text?: string | null) =>
  (text ?? "")
    // "a mandate" -> "an mandate" first, so the swap below reads "an application"
    .replace(/\b([aA]) (?=[mM]andate\b)/g, "$1n ")
    .replace(/\b(m|M)andate(s?)\b/g, (_, m: string, s: string) =>
      `${m === "M" ? "A" : "a"}pplication${s}`
    );

const groupPermissionsByResource = (permissions: Permission[]) => {
  const groups = new Map<string, Permission[]>();

  permissions.forEach((permission) => {
    const key = permission.resource_display || permission.resource || "Other";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(permission);
  });

  return Array.from(groups.entries()).map(([resource, items]) => ({
    resource,
    permissions: items,
  }));
};

const SectionHeader = ({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children?: React.ReactNode;
}) => (
  <Flex
    justifyContent="space-between"
    alignItems="flex-end"
    gap="16"
    mt={4}
    mb={2}
    pb={1}
    borderBottom="1px solid"
    borderBottomColor="gray.20"
  >
    <Box>
      <Paragraph fontWeight="bold" fontSize={2} m="0px">
        {title}
      </Paragraph>
      {subtitle && (
        <Paragraph color="gray.50" fontSize={1} m="0px">
          {subtitle}
        </Paragraph>
      )}
    </Box>
    {children}
  </Flex>
);

/**
 * Assigned users section, shared by create and edit (search box, count,
 * ProfileOrb rows, a Toggle per user) — purely local state in both cases.
 * Neither mode calls `/user/user_role/` directly: selections are collected
 * into `userIds` and sent as the `users` array in one shot on Create Role /
 * Save, per the confirmed request body (`{..., "users": [1, 2]}`) for both
 * POST and PATCH.
 */
const AssignedUsersSection = ({
  selectedUserIds,
  onChange,
}: {
  selectedUserIds: number[];
  onChange: (ids: number[]) => void;
}) => {
  const [search, setSearch] = useState("");
  const { data: allUsersData, isLoading } = useUsers();

  const users = unwrapList<any>(allUsersData?.data).filter((user) =>
    `${user.first_name} ${user.last_name} ${user.email}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const toggleUser = (userId: number, checked: boolean) => {
    onChange(
      checked
        ? Array.from(new Set([...selectedUserIds, userId]))
        : selectedUserIds.filter((id) => id !== userId)
    );
  };

  return (
    <Box>
      <SectionHeader
        title="Assigned users"
        subtitle={`${selectedUserIds.length} user${
          selectedUserIds.length === 1 ? "" : "s"
        } assigned`}
      >
        <Input
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          type="search"
          size="small"
          placeholder="Search users"
          maxWidth={200}
        />
      </SectionHeader>

      {isLoading && (
        <Flex justifyContent="center" p={4}>
          <Loader />
        </Flex>
      )}

      {!isLoading && users.length === 0 && (
        <Paragraph color="gray.60" mt={3}>
          No users found.
        </Paragraph>
      )}

      {!isLoading &&
        users.map((user) => (
          <Flex
            key={user.id}
            justifyContent="space-between"
            alignItems="center"
            py={2}
            borderBottom="1px solid"
            borderBottomColor="gray.10"
          >
            <Flex alignItems="center" gap="8">
              <ProfileOrb
                firstName={user.first_name}
                lastName={user.last_name}
                colour={user.colour}
              />
              <Box>
                <Paragraph m="0px" fontWeight="bold">
                  {user.first_name} {user.last_name}
                </Paragraph>
                <Paragraph m="0px" color="gray.50" fontSize={1}>
                  {user.email}
                </Paragraph>
              </Box>
            </Flex>
            <Toggle
              isChecked={selectedUserIds.includes(user.id)}
              onChange={(e) => toggleUser(user.id, e.target.checked)}
            />
          </Flex>
        ))}
    </Box>
  );
};

interface Props {
  isOpen: boolean;
  onRequestClose: () => void;
  role: AccessRole | null;
}

interface RoleFormValues {
  name: string;
  description: string;
  scope: string;
  /** `Permission.id`s — both POST and PATCH accept permission ids, not codenames. */
  permissionIds: number[];
  userIds: number[];
}

/**
 * Create/edit slider for Settings > Account & Access > Roles & Permissions
 * (SEG-S021).
 *
 * One scrolling form with three headed sections — Details, Permissions,
 * Assigned users — and a single sticky Create Role / Save button. Create
 * fires one POST with everything (`{name, description, scope, permissions,
 * users}`, per the confirmed request sample). Edit is seeded from GET
 * `/user/access_role/{id}/` (`useAccessRole` — the Role's own record, and the
 * source of truth for which permissions/users are already granted/assigned)
 * and fires one PATCH (`{description, permissions, users}` — PATCH doesn't
 * accept `name`/`scope` for an ordinary Custom Role, confirmed by a real
 * sample), then closes.
 *
 * System Roles (Staff/Manager/Admin/API Integration — `role.is_system`) are
 * fully read-only by default since they ship built-in, with two named
 * exceptions carved out per `SYSTEM_ROLE_EDIT_RULES` (Admin, Staff) — see
 * that constant for exactly what each unlocks. For those two, Details
 * becomes fully editable (Name/Scope included, not just Description), and
 * the PATCH payload includes `name`/`scope` too — a deliberate deviation
 * from the confirmed Custom-Role PATCH shape above, unconfirmed against a
 * real request/response for a System Role and worth verifying against the
 * live API before relying on it. Assigned users stays unconditionally
 * interactive for every System Role, exception or not, since assigning
 * people to them is the entire point of a built-in Role.
 */
export const RolePermissionsSlider = ({
  isOpen,
  onRequestClose,
  role,
}: Props) => {
  const { hasPermission } = useAuth();
  const hasRolesManage = hasPermission("roles.manage");
  const rolesManageDeniedMessage = "You don't have permission to manage roles";
  const mode = role ? "edit" : "create";
  const isSystemRole = role?.is_system === true;
  const systemRoleEditRule = isSystemRole
    ? SYSTEM_ROLE_EDIT_RULES[role?.name ?? ""]
    : undefined;
  // Name/Scope editable + sent on Save — only the two named System Roles
  // above; every ordinary Custom Role keeps Name/Scope locked (set once at
  // creation, per the confirmed PATCH shape). Structural only — NOT ANDed
  // with roles.manage: that would swap the editable form for the read-only
  // "System Roles ship built-in" copy even when the real blocker is the SEG
  // permission, which is misleading. The permission instead disables the
  // actual fields/checkboxes/buttons below (see hasRolesManage), never hides them.
  const canEditNameAndScope = Boolean(systemRoleEditRule?.details);
  const canEditPermissions = isSystemRole
    ? Boolean(systemRoleEditRule?.permissions)
    : true;
  const isPermissionsDisabled = !canEditPermissions || !hasRolesManage;
  const permissionsDisabledTitle =
    canEditPermissions && !hasRolesManage
      ? rolesManageDeniedMessage
      : undefined;
  // whether Save shows at all in edit mode — true for every Custom Role, and
  // for a System Role only if it has an edit rule at all (Admin or Staff,
  // regardless of which fields).
  const canSaveRole = !isSystemRole || Boolean(systemRoleEditRule);

  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);
  useEffect(() => {
    if (isOpen) setCollapsedGroups([]);
  }, [isOpen, role?.id]);

  const toggleGroupCollapsed = (resource: string) =>
    setCollapsedGroups((current) =>
      current.includes(resource)
        ? current.filter((key) => key !== resource)
        : [...current, resource]
    );

  // tenant-wide catalogue (name/description/resource grouping) for rendering
  // every permission row — unscoped, since a Role's own grants come from
  // useAccessRole below rather than this endpoint's unconfirmed `?role=`
  // filter (see Permission.is_granted).
  const { data: permissionsData, isLoading: isLoadingPermissions } =
    useAccessPermissions();
  const permissions = unwrapList<Permission>(permissionsData?.data);
  const permissionGroups = useMemo(
    () => groupPermissionsByResource(permissions),
    [permissions]
  );
  const allPermissionIds = useMemo(
    () => permissions.map(({ id }) => id),
    [permissions]
  );

  // the Role's own record — source of truth for which permissions/users are
  // already granted/assigned. Disabled in create mode (no role id yet).
  const { data: accessRoleData, refetch: refetchAccessRole } = useAccessRole(
    role?.id
  );
  const roleDetail = accessRoleData?.data;
  const initialPermissionIds = grantedPermissionIds(roleDetail?.permissions);
  const initialUserIds = assignedUserIds(roleDetail?.assigned_users);

  // this slider never unmounts between opens (SettingsRolesPermissions keeps
  // one instance around, `isOpen` only toggles visibility), so React Query's
  // normal "refetch on mount" never fires on a second Edit click for the same
  // Role — it's the same query instance serving whatever it last cached, even
  // after a Save changed the Role's real permissions/users. Force a refetch
  // every time the slider opens for an existing Role instead.
  useEffect(() => {
    if (isOpen && role?.id) {
      refetchAccessRole();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, role?.id]);

  const { mutate: createAccessRole, isLoading: isCreating } =
    useCreateAccessRole();
  const { mutate: updateAccessRole, isLoading: isUpdating } =
    useUpdateAccessRole();
  const { mutate: deleteAccessRole, isLoading: isDeleting } =
    useDeleteAccessRole();
  const isSaving = isCreating || isUpdating;
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

  const onDone = (message: string) => {
    notify(message, { type: "success" });
    queryClient.invalidateQueries(["accessRoles"]);
    if (role?.id) queryClient.invalidateQueries(["accessRole", role.id]);
    onRequestClose();
  };

  const handleSubmit = (values: RoleFormValues) => {
    if (mode === "edit" && role) {
      // PATCH doesn't accept name/scope for an ordinary Custom Role —
      // confirmed against a real request sample, see UpdateAccessRolePayload.
      // Permissions go as ids, same as create (confirmed against a real
      // PATCH payload). name/scope are only added for the two named System
      // Roles whose Details section lets you edit them (SYSTEM_ROLE_EDIT_RULES)
      // — an unconfirmed deviation, worth verifying against a real response.
      updateAccessRole(
        {
          id: role.id,
          description: values.description.trim() || null,
          permissions: values.permissionIds,
          users: values.userIds,
          ...(canEditNameAndScope
            ? { name: values.name.trim(), scope: values.scope }
            : {}),
        },
        {
          onSuccess: () => onDone("Role updated"),
          onError: (err: any) =>
            showError(err, "A problem occurred while updating the Role."),
        }
      );
    } else {
      createAccessRole(
        {
          name: values.name.trim(),
          description: values.description.trim() || null,
          scope: values.scope,
          permissions: values.permissionIds,
          users: values.userIds,
        },
        {
          onSuccess: () => onDone("Role created"),
          onError: (err: any) =>
            showError(err, "A problem occurred while creating the Role."),
        }
      );
    }
  };

  const handleConfirmDelete = () => {
    if (!role) return;
    setIsConfirmDeleteOpen(false);

    deleteAccessRole(
      { id: role.id },
      {
        onSuccess: () => onDone("Role deleted"),
        onError: (err: any) =>
          showError(err, deleteRoleErrorMessage(err)),
      }
    );
  };

  // A Role still held by users can't be deleted (the backend refuses with a
  // 409), so the button says why up front instead of failing on click.
  const assignedUserCount = role?.assigned_user_count ?? 0;
  const deleteBlockedTitle =
    assignedUserCount > 0
      ? `Remove the ${assignedUserCount} user${
          assignedUserCount === 1 ? "" : "s"
        } assigned to this Role before deleting it`
      : undefined;

  return (
    <Slider isOpen={isOpen} onRequestClose={onRequestClose} width="600px">
      <Box height="94%">
        <Formik
          enableReinitialize
          initialValues={{
            name: role?.name ?? "",
            description: role?.description ?? "",
            scope: role?.scope ?? "global",
            permissionIds: initialPermissionIds,
            userIds: initialUserIds,
          }}
          validationSchema={addAccessRoleValidation}
          onSubmit={handleSubmit}
        >
          {(formikProps) => {
            const grantedIds = formikProps.values.permissionIds;
            const countGranted = (ids: number[]) =>
              ids.filter((id) => grantedIds.includes(id)).length;

            // Adds or removes a batch of permission ids — backs the per-row,
            // per-group and the All checkboxes alike.
            const setPermissionsGranted = (ids: number[], checked: boolean) => {
              formikProps.setFieldValue(
                "permissionIds",
                checked
                  ? Array.from(new Set([...grantedIds, ...ids]))
                  : grantedIds.filter((id) => !ids.includes(id))
              );
            };

            const grantedCount = countGranted(allPermissionIds);

            return (
              // Scrolling sections on top, button bar pinned below as a
              // sibling — not `sticky` inside the scroll area, where the
              // area's padding left gaps around the bar for rows to show through.
              <Form
                onSubmit={formikProps.handleSubmit}
                style={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box flex={1} overflowY="auto" px={4} pt={3} pb={3}>
                  <Paragraph fontWeight="bold" fontSize={3} mt="0px">
                    {mode === "edit"
                      ? `Edit Role${role ? ` | ${role.name}` : ""}`
                      : "Create Role"}
                  </Paragraph>

                  {/* ---------- Details ---------- */}
                  <SectionHeader title="Details" />
                  {isSystemRole && !canEditNameAndScope ? (
                    <Box>
                      <DetailField label="Name" value={role?.name} />
                      <DetailField
                        mt={3}
                        label="Description"
                        value={role?.description}
                      />
                      <Flex gap="24" mt={3}>
                        <DetailField
                          flex={1}
                          label="Scope"
                          value={role?.scope_display}
                        />
                        <DetailField
                          flex={1}
                          label="Last modified"
                          value={formatDate(role?.updated_at)}
                        />
                      </Flex>
                      <Paragraph color="gray.50" fontSize={1} mt={3}>
                        System Roles ship built-in and can't be edited.
                      </Paragraph>
                    </Box>
                  ) : isSystemRole && canEditNameAndScope ? (
                    // the two named-System-Role exceptions
                    // (SYSTEM_ROLE_EDIT_RULES) — unlike an ordinary Custom
                    // Role's edit view below, Name and Scope are live inputs
                    // here too, not read-only DetailFields.
                    <Box>
                      <Flex gap="24">
                        <Input
                          flex={1}
                          label="Name"
                          name="name"
                          placeholder="Enter a Role name"
                          value={formikProps.values.name}
                          onChange={formikProps.handleChange}
                          isRequired
                          isDisabled={!hasRolesManage}
                          title={
                            !hasRolesManage
                              ? rolesManageDeniedMessage
                              : undefined
                          }
                          hasError={Boolean(formikProps.errors.name)}
                          errorMessage={formikProps.errors.name as string}
                        />
                        <Select
                          flex={1}
                          label="Scope"
                          name="scope"
                          value={
                            SCOPE_OPTIONS.find(
                              (option) =>
                                option.value === formikProps.values.scope
                            ) ?? null
                          }
                          options={SCOPE_OPTIONS}
                          onChange={(option: Option) =>
                            formikProps.setFieldValue("scope", option.value)
                          }
                          isInModal
                          isDisabled={!hasRolesManage}
                          title={
                            !hasRolesManage
                              ? rolesManageDeniedMessage
                              : undefined
                          }
                        />
                      </Flex>
                      <TextArea
                        mt={3}
                        label="Description"
                        name="description"
                        rows={3}
                        placeholder="What is this Role for?"
                        value={formikProps.values.description}
                        onChange={formikProps.handleChange}
                        isDisabled={!hasRolesManage}
                        title={
                          !hasRolesManage ? rolesManageDeniedMessage : undefined
                        }
                        hasError={Boolean(formikProps.errors.description)}
                        errorMessage={formikProps.errors.description as string}
                      />
                      <Flex gap="24" mt={3}>
                        <DetailField flex={1} label="Type" value="System" />
                        <DetailField
                          flex={1}
                          label="Last modified"
                          value={formatDate(role?.updated_at)}
                        />
                      </Flex>
                    </Box>
                  ) : mode === "edit" ? (
                    // PATCH doesn't accept name/scope (confirmed against a real
                    // request sample) — those are set only at creation, so
                    // they're shown read-only here.
                    <Box>
                      <Flex gap="24">
                        <DetailField flex={1} label="Name" value={role?.name} />
                        <DetailField
                          flex={1}
                          label="Scope"
                          value={role?.scope_display}
                        />
                      </Flex>
                      <TextArea
                        mt={3}
                        label="Description"
                        name="description"
                        rows={3}
                        placeholder="What is this Role for?"
                        value={formikProps.values.description}
                        onChange={formikProps.handleChange}
                        isDisabled={!hasRolesManage}
                        title={
                          !hasRolesManage ? rolesManageDeniedMessage : undefined
                        }
                        hasError={Boolean(formikProps.errors.description)}
                        errorMessage={formikProps.errors.description as string}
                      />
                      <Flex gap="24" mt={3}>
                        <DetailField flex={1} label="Type" value="Custom" />
                        <DetailField
                          flex={1}
                          label="Last modified"
                          value={formatDate(role?.updated_at)}
                        />
                      </Flex>
                    </Box>
                  ) : (
                    <Box>
                      <Flex gap="24">
                        <Input
                          flex={1}
                          label="Name"
                          name="name"
                          placeholder="Enter a Role name"
                          value={formikProps.values.name}
                          onChange={formikProps.handleChange}
                          isRequired
                          isDisabled={!hasRolesManage}
                          title={
                            !hasRolesManage
                              ? rolesManageDeniedMessage
                              : undefined
                          }
                          hasError={Boolean(formikProps.errors.name)}
                          errorMessage={formikProps.errors.name as string}
                        />
                        <Select
                          flex={1}
                          label="Scope"
                          name="scope"
                          value={
                            SCOPE_OPTIONS.find(
                              (option) =>
                                option.value === formikProps.values.scope
                            ) ?? null
                          }
                          options={SCOPE_OPTIONS}
                          isDisabled={!hasRolesManage}
                          title={
                            !hasRolesManage
                              ? rolesManageDeniedMessage
                              : undefined
                          }
                          onChange={(option: Option) =>
                            formikProps.setFieldValue("scope", option.value)
                          }
                          isInModal
                        />
                      </Flex>
                      <TextArea
                        mt={3}
                        label="Description"
                        name="description"
                        rows={3}
                        placeholder="What is this Role for?"
                        value={formikProps.values.description}
                        onChange={formikProps.handleChange}
                        isDisabled={!hasRolesManage}
                        title={
                          !hasRolesManage ? rolesManageDeniedMessage : undefined
                        }
                        hasError={Boolean(formikProps.errors.description)}
                        errorMessage={formikProps.errors.description as string}
                      />
                    </Box>
                  )}

                  {/* ---------- Permissions ---------- */}
                  <SectionHeader
                    title="Permissions"
                    subtitle={
                      permissions.length > 0
                        ? `${grantedCount} of ${permissions.length} granted`
                        : undefined
                    }
                  >
                    {permissions.length > 0 && (
                      <Checkbox
                        isChecked={grantedCount === allPermissionIds.length}
                        isIndeterminate={grantedCount > 0}
                        isDisabled={isPermissionsDisabled}
                        title={permissionsDisabledTitle}
                        onChange={(e) =>
                          setPermissionsGranted(
                            allPermissionIds,
                            e.target.checked
                          )
                        }
                        label={
                          <Paragraph m="0px" fontSize={1} fontWeight="bold">
                            All
                          </Paragraph>
                        }
                      />
                    )}
                  </SectionHeader>

                  {isLoadingPermissions && (
                    <Flex justifyContent="center" p={4}>
                      <Loader />
                    </Flex>
                  )}

                  {!isLoadingPermissions && permissions.length === 0 && (
                    <Paragraph color="gray.50">No permissions found.</Paragraph>
                  )}

                  {!isLoadingPermissions &&
                    permissionGroups.map((group) => {
                      const groupIds = group.permissions.map(({ id }) => id);
                      const groupGrantedCount = countGranted(groupIds);
                      const isCollapsed = collapsedGroups.includes(
                        group.resource
                      );

                      return (
                        <Box
                          key={group.resource}
                          border="1px solid"
                          borderColor="gray.20"
                          borderRadius={0}
                          mb={2}
                        >
                          <Flex
                            bg="gray.5"
                            px={2}
                            py={1}
                            justifyContent="space-between"
                            alignItems="center"
                            borderRadius={0}
                          >
                            <Checkbox
                              isChecked={
                                groupIds.length > 0 &&
                                groupGrantedCount === groupIds.length
                              }
                              isIndeterminate={groupGrantedCount > 0}
                              isDisabled={isPermissionsDisabled}
                              title={permissionsDisabledTitle}
                              onChange={(e) =>
                                setPermissionsGranted(
                                  groupIds,
                                  e.target.checked
                                )
                              }
                              label={
                                <Paragraph
                                  m="0px"
                                  fontSize={1}
                                  fontWeight="bold"
                                  style={{ textTransform: "uppercase" }}
                                >
                                  {toDisplayLabel(group.resource)}
                                </Paragraph>
                              }
                            />
                            <Flex
                              alignItems="center"
                              gap="8"
                              onClick={() =>
                                toggleGroupCollapsed(group.resource)
                              }
                              style={{ cursor: "pointer" }}
                              title={isCollapsed ? "Expand" : "Collapse"}
                            >
                              <Paragraph m="0px" fontSize={1} color="gray.50">
                                {groupGrantedCount}/{groupIds.length}
                              </Paragraph>
                              <Icon
                                Type={isCollapsed ? HiChevronDown : HiChevronUp}
                                color="gray.50"
                                size="16px"
                              />
                            </Flex>
                          </Flex>

                          {!isCollapsed && (
                            // two per row, like the Django admin's permission grid
                            <Flex
                              flexWrap="wrap"
                              px={2}
                              py={1}
                              borderTop="1px solid"
                              borderTopColor="gray.10"
                            >
                              {group.permissions.map((permission) => (
                                // padding goes on the Checkbox, not this Box: 50%
                                // plus padding (content-box) overflows and wraps
                                // every item onto its own line
                                <Box key={permission.id} width="50%">
                                  <Checkbox
                                    py={1}
                                    pr={2}
                                    alignValue="start"
                                    isChecked={grantedIds.includes(
                                      permission.id
                                    )}
                                    isDisabled={isPermissionsDisabled}
                                    title={permissionsDisabledTitle}
                                    onChange={(e) =>
                                      setPermissionsGranted(
                                        [permission.id],
                                        e.target.checked
                                      )
                                    }
                                    label={
                                      <Box>
                                        <Tooltip
                                          content={toDisplayLabel(
                                            permission.description
                                          )}
                                        >
                                          <Paragraph
                                            m="0px"
                                            fontSize={1}
                                            color="gray.80"
                                          >
                                            {toDisplayLabel(permission.name)}
                                          </Paragraph>
                                        </Tooltip>
                                        <Paragraph
                                          m="0px"
                                          fontSize={0}
                                          color="gray.50"
                                          fontFamily="monospace"
                                        >
                                          {permission.codename}
                                        </Paragraph>
                                        {permission.codename ===
                                          APPROVE_OWN_CODENAME &&
                                          grantedIds.includes(permission.id) &&
                                          role?.name !==
                                            API_INTEGRATION_ROLE_NAME && (
                                            <Paragraph
                                              m="0px"
                                              mt={1}
                                              fontSize={0}
                                              fontWeight={600}
                                              color="orange.50"
                                            >
                                              Lets holders approve their own
                                              applications, which bypasses
                                              maker/checker (self-approval
                                              prevention).
                                            </Paragraph>
                                          )}
                                      </Box>
                                    }
                                  />
                                </Box>
                              ))}
                            </Flex>
                          )}
                        </Box>
                      );
                    })}

                  {/* ---------- Assigned users ---------- */}
                  <AssignedUsersSection
                    selectedUserIds={formikProps.values.userIds}
                    onChange={(ids) =>
                      formikProps.setFieldValue("userIds", ids)
                    }
                  />
                </Box>

                {(mode === "create" || canSaveRole) && (
                  <Box
                    bg="white"
                    px={4}
                    py={3}
                    borderTop="1px solid"
                    borderTopColor="gray.20"
                  >
                    {mode === "create" ? (
                      <Button
                        type="submit"
                        size="large"
                        width="100%"
                        mb="0"
                        isDisabled={
                          isSaving || !formikProps.isValid || !hasRolesManage
                        }
                        title={
                          !hasRolesManage ? rolesManageDeniedMessage : undefined
                        }
                      >
                        {isSaving ? "Creating..." : "Create Role"}
                      </Button>
                    ) : (
                      <Button
                        type="submit"
                        size="large"
                        width="100%"
                        mb="0"
                        isDisabled={
                          isSaving ||
                          !formikProps.dirty ||
                          !formikProps.isValid ||
                          !hasRolesManage
                        }
                        title={
                          !hasRolesManage ? rolesManageDeniedMessage : undefined
                        }
                      >
                        {isSaving ? "Saving..." : "Save"}
                      </Button>
                    )}

                    {/* Delete stays hidden for every System Role, including
                        the two named Details/Permissions exceptions above —
                        deleting a built-in Role isn't part of what those
                        exceptions unlock. */}
                    {mode === "edit" && !isSystemRole && (
                      <Button
                        type="button"
                        variant="danger-text"
                        size="large"
                        width="100%"
                        mt="2"
                        mb="0"
                        isDisabled={
                          isDeleting ||
                          !hasRolesManage ||
                          assignedUserCount > 0
                        }
                        title={
                          !hasRolesManage
                            ? rolesManageDeniedMessage
                            : deleteBlockedTitle
                        }
                        onClick={() => setIsConfirmDeleteOpen(true)}
                      >
                        {isDeleting ? "Deleting..." : "Delete Role"}
                      </Button>
                    )}
                  </Box>
                )}
              </Form>
            );
          }}
        </Formik>

        <ConfirmModal
          isOpen={isConfirmDeleteOpen}
          onRequestClose={() => setIsConfirmDeleteOpen(false)}
          handleConfirm={handleConfirmDelete}
          title={role ? `Delete "${role.name}"?` : undefined}
          content={DELETE_ROLE_CONFIRM_TEXT}
        />
      </Box>
    </Slider>
  );
};
